import Button from 'components/Button';
import TooltipWrapper from 'components/Tooltip';
import {
    Children,
    FC,
    isValidElement,
    ReactElement,
    useMemo,
    useState,
} from 'react';
import {twMerge} from 'tailwind-merge';

import Section, {SectionProps} from './SectionSwitcherSection';

type SectionSwitcherFC = {
    Section: typeof Section;
} & FC<SectionSwitcherProps>;

type SectionSwitcherProps = {
    children?: ReactElement<SectionProps>[];
    className?: string;
};

const SectionSwitcher: SectionSwitcherFC = ({children, className}) => {
    const [activeTab, setActiveTab] = useState(() => {
        const activeIndex = Children.toArray(children).findIndex(
            (child) =>
                isValidElement(child) &&
                child.type === SectionSwitcher.Section &&
                child.props.isActive,
        );
        return activeIndex !== -1 ? activeIndex : 0;
    });

    const sectionSwitcherChildren = useMemo(
        () =>
            Children.toArray(children).filter(
                (child): child is ReactElement<SectionProps> =>
                    isValidElement(child) &&
                    child.type === SectionSwitcher.Section,
            ),
        [children],
    );

    const tabs = useMemo(
        () =>
            sectionSwitcherChildren.map(
                ({props: {label, permission}}, index) => (
                    <TooltipWrapper
                        key={label}
                        message={
                            permission?.allowed === false
                                ? permission?.failureReason ||
                                  'Section disabled'
                                : undefined
                        }
                    >
                        <Button
                            aria-selected={index === activeTab}
                            className={twMerge(
                                'px-4 py-2 text-light-secondary border font-semibold border-grey-400 bg-grey-200 rounded-t rounded-b-none hover:bg-white',
                                index === activeTab
                                    ? 'bg-white border-b-white text-light-primary'
                                    : 'hover:text-green-700 active:bg-grey-300',
                                permission?.allowed === false &&
                                    index !== activeTab &&
                                    'cursor-not-allowed bg-dark-disabled text-light-disabled hover:text-light-disabled active:bg-dark-disabled',
                            )}
                            disabled={permission?.allowed === false}
                            kind="custom"
                            onClick={() => setActiveTab(index)}
                            role="tab"
                        >
                            {label}
                        </Button>
                    </TooltipWrapper>
                ),
            ),
        [sectionSwitcherChildren, activeTab],
    );

    const activeSection = useMemo(
        () => sectionSwitcherChildren[activeTab],
        [sectionSwitcherChildren, activeTab],
    );

    return (
        <div className={twMerge('flex flex-col', className)}>
            <div className="relative -bottom-px flex gap-1" role="tablist">
                {tabs}
            </div>
            <div
                className="rounded border border-grey-400 p-4"
                // rounded-tl-none doesn't seem to correctly work  hence the use of style
                style={{borderTopLeftRadius: 0}}
            >
                {activeSection}
            </div>
        </div>
    );
};

SectionSwitcher.Section = Section;

export default SectionSwitcher;
